import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import {announce} from '@react-aria/live-announcer'
import Markdown from 'react-markdown'

import InnosearchMessageSvg from '@/assets/icons/innosearch-message.svg?react'
import loading from '@/assets/lotties/loading.lottie'
import FollowUpTopicsV2 from '@/components/ChatSection/componnents/FollowUpTopicsV2/FollowUpTopicsV2'
import {DEFAULT_SEND_MESSAGE_PLACEHOLDER} from '@/components/ChatSection/consts'
import type {UseMessagesReturn} from '@/components/ChatSection/hooks/useMessages'
import {latestPromptsAtom} from '@/components/ChatSection/states'
import getFollowUpPrompts2FromMessage from '@/lib/assistantMessage/getFollowUpPrompts2FromMessage'
import getMetadataFromMessage from '@/lib/assistantMessage/getMetadataFromMessage'
import mergeTextAndDataNode from '@/lib/assistantMessage/mergeTextAndDataNode'
import splitProductsFromMessage, {
  type RawProductData,
} from '@/lib/assistantMessage/splitProductsFromMessage'
import type {RawQuestionData} from '@/lib/assistantMessage/splitQuestionFromMessage'
import splitQuestionFromMessage from '@/lib/assistantMessage/splitQuestionFromMessage'
import type {Message, MinimalMessage} from '@/models/messageModel'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import type {FollowUpPromptsV2} from '@/views/Chat/types'

import style from './InnoSearchMessage.module.scss'
import InnoSearchMessageToolbar from './InnoSearchMessageToolbar/InnoSearchMessageToolbar'
import InnoSearchQuestion from './InnoSearchQuestion/InnoSearchQuestion'

const SuggestedProducts = lazy(
  async () =>
    import(
      '@/components/ChatSection/componnents/InnoSearchMessage/SuggestedProducts/SuggestedProducts'
    ),
)

function LinkRenderer(props: PropsWithChildren<{href?: string}>) {
  return (
    <a href={props.href} target='_blank' rel='noreferrer'>
      {props.children}
    </a>
  )
}

function ImageRenderer(props: {src?: string; alt?: string; title?: string}) {
  if (
    props.src === 'generatingResponse' &&
    props.alt === 'Generating response' &&
    props.title === props.alt
  )
    return (
      <span className={style.LoadingWrapper}>
        <DotLottieReact
          src={loading}
          autoplay
          loop
          className={style.Loading}
          aria-label='Generating response...'
        />
      </span>
    )
  return (
    <img
      className={style.InnoSearchMessageImage}
      src={props.src}
      alt={props.alt}
      aria-label={props.title}
      loading='lazy'
    />
  )
}

const GENERATING_RESPONSE_MD_TEXT =
  '![Generating response](generatingResponse "Generating response")'

const magicStringRegexString = '!@#.*!@#\n' // Use this to construct regex to avoid stateful regex problems

function parseMessage({message, isLoading}: {message: string; isLoading: boolean}) {
  // console.time('parseMessage')
  const prompts: FollowUpPromptsV2 = []

  let loadingMessage = ''

  let preProcessedMessage: string

  // Remove all loading response text from message
  preProcessedMessage = message.replaceAll(
    new RegExp(magicStringRegexString, 'g'),
    magicRawValue => {
      try {
        const magicValue = JSON.parse(magicRawValue.slice(3, -4)) as Record<string, unknown>
        if ('type' in magicValue && magicValue.type === 'loading_begin') {
          loadingMessage = String(magicValue.message)
        }
      } catch (e) {
        console.error('Cannot parse magic value', magicRawValue, e)
      }
      return ''
    },
  )

  // If message is still loading, and the end of it is the loading text, then add it back
  if (isLoading && new RegExp(`${magicStringRegexString}$`).test(message)) {
    preProcessedMessage = `${preProcessedMessage}*${loadingMessage}*${GENERATING_RESPONSE_MD_TEXT}\n\n`
  }

  // If there is absolutely nothing to display, display a loading indicator
  if (preProcessedMessage.length === 0) {
    preProcessedMessage = GENERATING_RESPONSE_MD_TEXT
  }

  let result: (string | RawProductData | RawQuestionData)[]

  // console.groupCollapsed('GET PRODUCTS FROM MESSAGE')
  result = mergeTextAndDataNode(splitProductsFromMessage(preProcessedMessage, false))
  // console.groupEnd()

  result = result.reduce<(string | RawProductData | RawQuestionData)[]>((acc, thisItem) => {
    if (typeof thisItem === 'string') {
      acc.push(...mergeTextAndDataNode(splitQuestionFromMessage(thisItem)))
    } else {
      acc.push(thisItem)
    }
    return acc
  }, [])

  // console.groupCollapsed('GET FOLLOW-UP PROMPTS FROM MESSAGE')
  result = result.map(res => {
    if (typeof res === 'string') {
      res = getFollowUpPrompts2FromMessage(res, false, prompts)
      return res
    }
    return res
  })
  // console.groupEnd()

  // Add loading image to the end of string part
  // if (isLoading) {
  //   const lastStringIndex = result.findLastIndex(item => typeof item === 'string')
  //   if (lastStringIndex !== -1) {
  //     if ((result[lastStringIndex] as string).endsWith('\n')) {
  //       result[lastStringIndex] =
  //         (result[lastStringIndex] as string).slice(0, -2) + GENERATING_RESPONSE_MD_TEXT + '\n'
  //     } else {
  //       result[lastStringIndex] = (result[lastStringIndex] as string) + GENERATING_RESPONSE_MD_TEXT
  //     }
  //   } else {
  //     result.push(GENERATING_RESPONSE_MD_TEXT)
  //   }
  // }

  // console.timeEnd('parseMessage')

  return {
    result,
    prompts,
  }
}

interface Props {
  message: Message | MinimalMessage
  submitMessage: UseMessagesReturn['submitMessage']
  isLoading: boolean
  isLatest: boolean
  setQ: Dispatch<SetStateAction<string>>
  setSendMessagePlaceholder: Dispatch<SetStateAction<string>>
}

// TODO: offload parsing to worker
export default memo<Props>(function InnoSearchMessage({
  message,
  submitMessage,
  setQ,
  setSendMessagePlaceholder,
  isLoading = false,
  isLatest = false,
}) {
  const messageContentRef = useRef<HTMLDivElement>(null)

  const setLatestPrompts = useSetAtom(latestPromptsAtom)

  const {leftOverMessage} = getMetadataFromMessage(message.content, true)

  const {result, prompts} = parseMessage({
    isLoading,
    message: leftOverMessage,
  })

  if (isLatest && prompts.length > 0) setLatestPrompts(prompts)

  // Note: I don't lift this logic to Chat component because I don't wanna Chat component to call parseMessage
  // The component memoized and don't depend on `sendMessagePlaceholder` so this is guaranteed not cause infinite render loop
  useEffect(() => {
    if (isLatest && !isLoading) {
      if (result.find(r => typeof r !== 'string' && r.type === 'question')) {
        setSendMessagePlaceholder('Or input your opinion here')
      } else {
        setSendMessagePlaceholder(DEFAULT_SEND_MESSAGE_PLACEHOLDER)
      }
    }

    return () => {
      setSendMessagePlaceholder(DEFAULT_SEND_MESSAGE_PLACEHOLDER)
    }
  }, [isLatest, isLoading, result, setSendMessagePlaceholder])

  useEffect(() => {
    if (!isLoading && isLatest) {
      announce('Innosearch has generated the response...', 'assertive')
      document.getElementById('latest-message')?.focus()
      const messageContentElements = document.querySelectorAll(`.${style.InnoSearchMessageContent}`)

      // Let's retrieve the last element from the NodeList
      const latestMessageContent = messageContentElements[messageContentElements.length - 1]

      if (latestMessageContent?.textContent) {
        announce(latestMessageContent.textContent, 'assertive')
      }
    }
  }, [isLoading, isLatest])

  return (
    <div
      {...(isLatest ? skipTargetProps('latest-message') : {})}
      aria-label={`InnoSearch ${isLatest ? 'latest ' : ''}reply`}
      role={isLatest ? 'region' : undefined}
      tabIndex={-1}
    >
      <VisuallyHidden as='h3'>{`InnoSearch ${isLatest ? 'latest ' : ''}reply`}</VisuallyHidden>
      <div className={style.InnoSearchMessage}>
        <div className={style.InnoSearchIcon} aria-hidden>
          <InnosearchMessageSvg />
        </div>
        <div className={style.InnoSearchMessageContent}>
          <div ref={messageContentRef}>
            {result.map((res, i) => {
              if (typeof res === 'string') {
                return (
                  <Markdown key={i} components={{a: LinkRenderer, img: ImageRenderer}}>
                    {res}
                  </Markdown>
                )
              } else if (res.type === 'products') {
                return (
                  <Suspense key={i}>
                    <SuggestedProducts rawProducts={res.data} submitMessage={submitMessage} />
                  </Suspense>
                )
              }
              return (
                <InnoSearchQuestion
                  key={i}
                  question={res.data}
                  setQ={setQ}
                  isDisabled={!isLatest}
                  submitMessage={submitMessage}
                />
              )
            })}
          </div>
          {!isLoading && (
            <InnoSearchMessageToolbar
              messageContentRef={messageContentRef}
              canRegenerate={isLatest}
              action={'action' in message ? message.action : undefined}
              id={'id' in message ? message.id : undefined}
              submitMessage={submitMessage}
            />
          )}
        </div>
      </div>
      {prompts.length > 0 && <FollowUpTopicsV2 topics={prompts} submitMessage={submitMessage} />}
    </div>
  )
})
